/**
 * Geometria da grade horária da Agenda — só aritmética de minutos, sem React.
 *
 * Tudo em MINUTOS a partir da meia-noite local do dia da coluna. É o que a
 * grade precisa para converter "reunião das 14h às 15h" em `top`/`height`, e
 * manter isso puro deixa testável o que mais dá dor de cabeça num calendário:
 * compromisso que atravessa a meia-noite, bloqueio de vários dias, três
 * reuniões ao mesmo tempo.
 */

import { somaDias } from "./agenda-utils";

export type Intervalo = { ini: number; fim: number };

export const MINUTOS_NO_DIA = 24 * 60;

/**
 * Pedaço de [inicio, fim) que cai dentro do dia `dia`, em minutos desde a
 * meia-noite local. `null` quando não toca o dia.
 *
 * Férias de 20 a 27 viram sete recortes de dia inteiro; uma reunião que começa
 * às 23h e acaba à 1h vira dois pedaços, um em cada dia.
 */
export function recorteNoDia(
  inicio: Date | string,
  fim: Date | string,
  dia: Date,
): Intervalo | null {
  const a = typeof inicio === "string" ? new Date(inicio) : inicio;
  const b = typeof fim === "string" ? new Date(fim) : fim;
  const inicioDoDia = new Date(dia);
  inicioDoDia.setHours(0, 0, 0, 0);
  const fimDoDia = somaDias(inicioDoDia, 1);

  const ini = Math.max(a.getTime(), inicioDoDia.getTime());
  const end = Math.min(b.getTime(), fimDoDia.getTime());
  if (end <= ini) return null;

  const base = inicioDoDia.getTime();
  return {
    ini: Math.round((ini - base) / 60_000),
    fim: Math.min(MINUTOS_NO_DIA, Math.round((end - base) / 60_000)),
  };
}

/** "HH:mm" → minutos desde a meia-noite. */
export function hhmmParaMinutos(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Une intervalos que se tocam ou sobrepõem, em ordem. */
export function unir(intervalos: Intervalo[]): Intervalo[] {
  const ordenados = [...intervalos]
    .filter((i) => i.fim > i.ini)
    .sort((a, b) => a.ini - b.ini);
  const out: Intervalo[] = [];
  for (const i of ordenados) {
    const ultimo = out[out.length - 1];
    if (ultimo && i.ini <= ultimo.fim) ultimo.fim = Math.max(ultimo.fim, i.fim);
    else out.push({ ...i });
  }
  return out;
}

/** O que sobra de [ini, fim) tirando `ocupados`. */
export function complemento(janela: Intervalo, ocupados: Intervalo[]): Intervalo[] {
  const out: Intervalo[] = [];
  let cursor = janela.ini;
  for (const o of unir(ocupados)) {
    if (o.fim <= cursor) continue;
    if (o.ini >= janela.fim) break;
    if (o.ini > cursor) out.push({ ini: cursor, fim: o.ini });
    cursor = Math.max(cursor, o.fim);
  }
  if (cursor < janela.fim) out.push({ ini: cursor, fim: janela.fim });
  return out;
}

export type Posicao = { coluna: number; total: number };

/**
 * Distribui compromissos simultâneos lado a lado.
 *
 * Itens que se sobrepõem (direta ou encadeadamente) formam um grupo; dentro do
 * grupo cada um vai para a primeira "faixa" livre, e todos os do grupo dividem
 * a largura pelo número de faixas que o grupo precisou. É o mesmo desenho do
 * Google Calendar: duas reuniões às 10h ficam meio a meio, e a das 11h que não
 * bate com nenhuma volta a ocupar a coluna inteira.
 *
 * Devolve na mesma ordem da entrada.
 */
export function empacotar(itens: Intervalo[]): Posicao[] {
  const ordem = itens
    .map((it, idx) => ({ it, idx }))
    .sort((a, b) => a.it.ini - b.it.ini || a.it.fim - b.it.fim);

  const out: Posicao[] = new Array(itens.length);
  let grupo: number[] = [];
  let fimDasFaixas: number[] = [];
  let fimDoGrupo = -Infinity;

  const fecharGrupo = () => {
    for (const idx of grupo) out[idx].total = fimDasFaixas.length;
    grupo = [];
    fimDasFaixas = [];
  };

  for (const { it, idx } of ordem) {
    if (grupo.length > 0 && it.ini >= fimDoGrupo) fecharGrupo();
    let faixa = fimDasFaixas.findIndex((fim) => fim <= it.ini);
    if (faixa === -1) {
      faixa = fimDasFaixas.length;
      fimDasFaixas.push(it.fim);
    } else {
      fimDasFaixas[faixa] = it.fim;
    }
    out[idx] = { coluna: faixa, total: 0 };
    grupo.push(idx);
    fimDoGrupo = Math.max(fimDoGrupo, it.fim);
  }
  if (grupo.length > 0) fecharGrupo();
  return out;
}

/**
 * Faixa de horas que a grade mostra.
 *
 * Começa no horário comercial (7h–20h) e só se alarga quando há algo fora
 * dele — assim quem tem tudo entre 9h e 18h não rola uma grade de 24 linhas,
 * e quem tem a reunião das 21h ainda a vê.
 */
export function faixaDeHoras(
  ocupados: Intervalo[],
  padrao: { ini: number; fim: number } = { ini: 7, fim: 20 },
): { ini: number; fim: number } {
  let ini = padrao.ini;
  let fim = padrao.fim;
  for (const o of ocupados) {
    ini = Math.min(ini, Math.floor(o.ini / 60));
    fim = Math.max(fim, Math.ceil(o.fim / 60));
  }
  return { ini: Math.max(0, ini), fim: Math.min(24, Math.max(fim, ini + 1)) };
}
